/**
 * Match identity (§4.4, R23, R24): the visible match number and, for tournament games, the
 * tournament, round, match number within the tournament and court. Pure functions only;
 * the caller injects the date and the random source.
 */

import type { CategoryCode } from './category';
import { isCategoryCode } from './category';

export type MatchType = 'single' | 'tournament' | 'championship';

/** Level of a tournament match. */
export type RoundCode = 'L' | 'KO' | 'R16' | 'QF' | 'SF' | '3P' | 'F';

/** In bracket order: league first, final last. */
export const ROUND_CODES: readonly RoundCode[] = ['L', 'KO', 'R16', 'QF', 'SF', '3P', 'F'];

export interface TournamentRef {
  id: string;
  name: string;
  /** 3–8 capital letters / digits, e.g. 'CBL26'. */
  code: string;
}

/** The championships the app knows. Each is an entry in championship/championship.ts. */
export type ChampionshipId = 'tnba';

/** The championship and year of a championship game (§5.6). */
export interface ChampionshipRef {
  id: ChampionshipId;
  year: number;
}

export interface MatchInfo {
  /** Visible match number: 'M-20261104-7K2Q', 'CBL26-QF-14' or 'TNBA26-U16B-QF-14'. */
  number: string;
  type: MatchType;
  tournament?: TournamentRef;
  championship?: ChampionshipRef;
  /** Missing for games created before categories, and optional on a single match. */
  category?: CategoryCode;
  /** Match number within the tournament (1, 2, 3 …). */
  matchNo?: number;
  round?: RoundCode;
  /** Court name, e.g. 'Court 2'. Not part of the match number. */
  court?: string;
  venue?: string;
  officials?: MatchOfficials;
  /** Post-match routine (§5.4): anything the scorer wants on record. */
  notes?: string;
  /** The game was played under protest. */
  protest?: boolean;
  signOff?: MatchSignOff;
}

/** Typed names at the end of the game. */
export interface MatchSignOff {
  crewChief?: string;
  homeCaptain?: string;
  awayCaptain?: string;
}

const SIGN_OFF_ROLES: readonly (keyof MatchSignOff)[] = ['crewChief', 'homeCaptain', 'awayCaptain'];

/** Referees (Art. 46) and table officials (Art. 48-51). All optional. */
export interface MatchOfficials {
  crewChief?: string;
  umpire1?: string;
  umpire2?: string;
  scorer?: string;
  timer?: string;
  shotClockOperator?: string;
}

export const OFFICIAL_ROLES: readonly (keyof MatchOfficials)[] = [
  'crewChief',
  'umpire1',
  'umpire2',
  'scorer',
  'timer',
  'shotClockOperator',
];

/** Details that can be filled in or changed at any time, even after the game. */
export interface MatchDetails {
  court?: string;
  venue?: string;
  officials?: MatchOfficials;
  notes?: string;
  protest?: boolean;
  signOff?: MatchSignOff;
}

export const MAX_DETAIL_LENGTH = 40;
export const MAX_NOTES_LENGTH = 500;

/** True when every text in the details fits (blank is fine: it clears the field). */
export function validMatchDetails(d: MatchDetails): boolean {
  const ok = (v: unknown, max: number) =>
    v === undefined || (typeof v === 'string' && v.trim().length <= max);
  if (!ok(d.court, MAX_COURT_LENGTH) || !ok(d.venue, MAX_DETAIL_LENGTH)) return false;
  if (!ok(d.notes, MAX_NOTES_LENGTH)) return false;
  if (d.protest !== undefined && typeof d.protest !== 'boolean') return false;
  const o = d.officials;
  if (o !== undefined && !OFFICIAL_ROLES.every((role) => ok(o[role], MAX_DETAIL_LENGTH))) {
    return false;
  }
  const s = d.signOff;
  return s === undefined || SIGN_OFF_ROLES.every((role) => ok(s[role], MAX_DETAIL_LENGTH));
}

/** `match` with `details` applied. Blank values remove a field; names are trimmed. */
export function withMatchDetails(match: MatchInfo, details: MatchDetails): MatchInfo {
  const next: MatchInfo = { ...match };
  if (details.protest === true) next.protest = true;
  else if (details.protest === false) delete next.protest;
  if (details.signOff !== undefined) {
    const signOff: MatchSignOff = { ...match.signOff };
    for (const role of SIGN_OFF_ROLES) {
      const value = details.signOff[role]?.trim();
      if (value === undefined) continue;
      if (value === '') delete signOff[role];
      else signOff[role] = value;
    }
    if (Object.keys(signOff).length > 0) next.signOff = signOff;
    else delete next.signOff;
  }
  for (const key of ['court', 'venue', 'notes'] as const) {
    const value = details[key]?.trim();
    if (value === undefined) continue;
    if (value === '') delete next[key];
    else next[key] = value;
  }
  if (details.officials !== undefined) {
    const officials: MatchOfficials = { ...match.officials };
    for (const role of OFFICIAL_ROLES) {
      const value = details.officials[role]?.trim();
      if (value === undefined) continue;
      if (value === '') delete officials[role];
      else officials[role] = value;
    }
    if (Object.keys(officials).length > 0) next.officials = officials;
    else delete next.officials;
  }
  return next;
}

export const MAX_MATCH_NO = 999;
export const MAX_COURT_LENGTH = 24;

/** Crockford-style alphabet: no I, L, O or U, so codes are easy to read aloud. */
const CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));

export function isRoundCode(value: unknown): value is RoundCode {
  return typeof value === 'string' && (ROUND_CODES as readonly string[]).includes(value);
}

/** Knockout rounds can never end tied (§4.5). */
export function isKnockoutRound(round: RoundCode | undefined): boolean {
  return round !== undefined && round !== 'L';
}

export function isValidTournamentCode(code: unknown): code is string {
  return typeof code === 'string' && /^[A-Z0-9]{3,8}$/.test(code);
}

/**
 * Single match number: `M-<yyyymmdd>-<4 chars>` from the local date of `at`.
 * `random` returns values in [0, 1) (Math.random in the app, a fixed sequence in tests).
 */
export function singleMatchNumber(at: number, random: () => number): string {
  const d = new Date(at);
  const date = `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
  let code = '';
  for (let i = 0; i < 4; i++) {
    const r = Math.min(Math.max(random(), 0), 0.999999);
    code += CODE_ALPHABET[Math.floor(r * CODE_ALPHABET.length)];
  }
  return `M-${date}-${code}`;
}

/**
 * Suggested tournament code: initials of the name's Latin words (up to 6) plus the 2-digit
 * year, e.g. 'Chennai Basketball League', 2026 → 'CBL26'. Names without Latin letters get
 * 'HS' + year. The user can edit it.
 */
export function suggestTournamentCode(name: string, year: number): string {
  const initials = (name.normalize('NFKD').match(/[A-Za-z0-9]+/g) ?? [])
    .map((w) => w[0]!.toUpperCase())
    .join('')
    .slice(0, 6);
  const yy = pad2(((year % 100) + 100) % 100);
  return `${initials.length >= 1 ? initials : 'HS'}${yy}`.padEnd(3, 'X');
}

/** `<code>-<round>-<number>`, e.g. 'CBL26-QF-14'. */
export function tournamentMatchNumber(code: string, round: RoundCode, matchNo: number): string {
  return `${code}-${round}-${pad2(matchNo)}`;
}

export function createSingleMatch(at: number, random: () => number): MatchInfo {
  return { number: singleMatchNumber(at, random), type: 'single' };
}

export type MatchInfoError =
  | 'INVALID_TOURNAMENT_CODE'
  | 'INVALID_TOURNAMENT_NAME'
  | 'INVALID_MATCH_NO'
  | 'INVALID_ROUND'
  | 'INVALID_COURT'
  | 'INVALID_CATEGORY'
  | 'INVALID_YEAR';

export interface TournamentMatchInput {
  tournament: TournamentRef;
  round: RoundCode;
  matchNo: number;
  court?: string;
  category?: CategoryCode;
}

/** The first problem with a match's round, number and court, or null. */
export function matchPlaceProblem(
  round: unknown,
  matchNo: number,
  court: string | undefined,
): MatchInfoError | null {
  if (!isRoundCode(round)) return 'INVALID_ROUND';
  if (!Number.isInteger(matchNo) || matchNo < 1 || matchNo > MAX_MATCH_NO) {
    return 'INVALID_MATCH_NO';
  }
  if (court !== undefined && court.trim().length > MAX_COURT_LENGTH) return 'INVALID_COURT';
  return null;
}

/** Builds a tournament game's MatchInfo, or the first problem with the input. */
export function createTournamentMatch(
  input: TournamentMatchInput,
): { ok: true; match: MatchInfo } | { ok: false; error: MatchInfoError } {
  const { tournament, round, matchNo } = input;
  if (!isValidTournamentCode(tournament.code))
    return { ok: false, error: 'INVALID_TOURNAMENT_CODE' };
  if (typeof tournament.name !== 'string' || tournament.name.trim() === '') {
    return { ok: false, error: 'INVALID_TOURNAMENT_NAME' };
  }
  const problem = matchPlaceProblem(round, matchNo, input.court);
  if (problem !== null) return { ok: false, error: problem };
  const court = input.court?.trim();
  const { category } = input;
  if (category !== undefined && !isCategoryCode(category)) {
    return { ok: false, error: 'INVALID_CATEGORY' };
  }
  return {
    ok: true,
    match: {
      number: tournamentMatchNumber(tournament.code, round, matchNo),
      type: 'tournament',
      tournament: { ...tournament, name: tournament.name.trim() },
      matchNo,
      round,
      ...(category !== undefined ? { category } : {}),
      ...(court ? { court } : {}),
    },
  };
}
